import React, { useEffect, useState } from 'react';
import { authFetch } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { StickyNote, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const ClientNotesCard = ({ userId }) => {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const base = `${API_BASE}/api/admin/clients/${userId}/notes`;

  useEffect(() => {
    authFetch(base).then((r) => (r.ok ? r.json() : [])).then(setNotes).catch(() => setNotes([]));
  }, [base]);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      const res = await authFetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save note');
      setNotes([data, ...notes]);
      setText('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    const res = await authFetch(`${base}/${id}`, { method: 'DELETE' });
    if (res.ok) setNotes(notes.filter((n) => n.id !== id));
    else toast.error('Could not delete note');
  };

  return (
    <Card className="bg-[#0f1724] border-white/10" data-testid="client-notes-card">
      <CardHeader>
        <CardTitle className="text-lg text-white flex items-center gap-2">
          <StickyNote className="h-5 w-5 text-[#C6A14A]" /> Private Notes
        </CardTitle>
        <CardDescription className="text-[#E8E4DE]/60">Only admins can see these — never the client.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={add} className="space-y-2">
          <Textarea rows={3} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Prefers texts after 3pm · Gate code 4455"
            className="bg-[#050816] border-white/10 text-white placeholder:text-white/30" data-testid="client-note-input" />
          <Button type="submit" size="sm" disabled={saving || !text.trim()}
            className="bg-[#C6A14A] text-[#050816] hover:bg-[#d4b25c]" data-testid="client-note-add-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add Note
          </Button>
        </form>
        {notes.length === 0 ? (
          <p className="text-sm text-[#E8E4DE]/40" data-testid="client-notes-empty">No notes yet</p>
        ) : (
          <ul className="space-y-2" data-testid="client-notes-list">
            {notes.map((n) => (
              <li key={n.id} className="group p-3 rounded-lg bg-[#050816]/40 border border-white/5" data-testid={`client-note-${n.id}`}>
                <div className="flex items-start gap-3">
                  <p className="flex-1 text-[#E8E4DE] whitespace-pre-wrap text-sm">{n.text}</p>
                  <button type="button" onClick={() => remove(n.id)} aria-label="Delete note"
                    className="text-[#E8E4DE]/40 hover:text-red-400 transition-colors" data-testid={`client-note-delete-${n.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-[#E8E4DE]/40 mt-1.5">
                  {n.author_name || 'Admin'} · {new Date(n.created_at).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
